import { z } from 'zod';

import { CoordinatesSchema } from './shared-schemas.js';

/**
 * The Zod schema for the input of the `getWeather` tool.
 */
export const GetWeatherRequest = z
  .object({
    location: z.string().optional().describe('City or region name'),
    locationFallbacks: z.array(z.string()).optional().describe('Optional fallback location candidates'),
    coordinates: CoordinatesSchema.optional().describe('Coordinates to query weather directly'),
    days: z.number().min(1).max(14).optional().default(7).describe('Number of forecast days'),
    units: z.enum(['metric', 'imperial']).optional().default('metric').describe('Temperature units'),
    startDate: z.string().optional().describe('Start date (YYYY-MM-DD) for filtering forecast'),
    endDate: z.string().optional().describe('End date (YYYY-MM-DD, inclusive) for filtering forecast'),
  })
  .refine((value) => !!(value.coordinates || value.location?.trim()), {
    message: 'Either location or coordinates is required',
    path: ['location'],
  });

/**
 * The Zod schema for the structure of a single day's weather forecast.
 */
export const DailyForecastSchema = z.object({
  date: z.string().describe('Date in YYYY-MM-DD format'),
  temperature: z
    .object({ min: z.number().describe('Minimum temperature'), max: z.number().describe('Maximum temperature') })
    .describe('Temperature range'),
  wind: z
    .object({ speed: z.number().describe('Wind speed'), direction: z.string().describe('Wind direction') })
    .optional()
    .describe('Wind information'),
  weather: z
    .array(z.object({ description: z.string().describe('Weather description') }))
    .describe('Weather conditions'),
});

export type DailyForecast = z.infer<typeof DailyForecastSchema>;

export const WindSpeedSchema = z.object({
  speed: z.number().describe('Wind speed'),
  deg: z.number().describe('Wind direction'),
});

export const WeatherDescriptionSchema = z.object({
  main: z.string().describe('Weather main'),
  description: z.string().describe('Weather description'),
});

export type WeatherDescription = z.infer<typeof WeatherDescriptionSchema>;

export const WeatherMainSchema = z.object({
  temp: z.number().describe('Temperature'),
  feels_like: z.number().describe('Feels like temperature').optional(),
  humidity: z.number().describe('Humidity').optional(),
  pressure: z.number().describe('Pressure').optional(),
  uvi: z.number().describe('UV index').optional(),
});

export type WeatherMain = z.infer<typeof WeatherMainSchema>;

export const WeatherResponseSchema = z.object({
  name: z.string().describe('City name'),
  main: WeatherMainSchema,
  wind: WindSpeedSchema,
  weather: z.array(WeatherDescriptionSchema),
  visibility: z.number().describe('Visibility').optional(),
});

export type WeatherResponse = z.infer<typeof WeatherResponseSchema>;

export const WeatherForecastItemSchema = z.object({
  dt: z.number().describe('Timestamp'),
  main: WeatherMainSchema,
  wind: WindSpeedSchema,
  weather: z.array(WeatherDescriptionSchema),
});

export type WeatherForecastItem = z.infer<typeof WeatherForecastItemSchema>;

export const WeatherForecastResponseSchema = z.object({ list: z.array(WeatherForecastItemSchema) });

export type WeatherForecastResponse = z.infer<typeof WeatherForecastResponseSchema>;

export const CurrentWeatherSchema = z.object({
  temperature: z.number(),
  feelsLike: z.number().optional(),
  condition: z.string().optional(),
  description: z.string().optional(),
  humidity: z.number().optional(),
  windSpeed: z.number().optional(),
  windDirection: z.number().optional(),
  pressure: z.number().optional(),
  visibility: z.number().optional(),
  units: z.enum(['metric', 'imperial']).optional(),
  uvIndex: z.number().optional(),
});

export const GetWeatherResponse = z.object({
  location: z.string().describe('City name'),
  current: CurrentWeatherSchema,
  forecast: z.array(DailyForecastSchema),
  units: z.enum(['metric', 'imperial']),
});

export type GetWeatherResponse = z.infer<typeof GetWeatherResponse>;
